import type { ResourceListProps } from '@shopify/polaris';
import type { TemplateType } from '~/types/template';
import type { IThemePageManagement } from '../types';
import type { ReactNode } from 'react';

import { createContext, useContext, useMemo, useState } from 'react';
import { useQueryThemePageManagement } from '../hooks';

interface IThemePageManagementContext {
  queryValue: string | undefined;
  setQueryValue: (value: string) => void;
  pageTypes: TemplateType[] | undefined;
  setPageTypes: (value: TemplateType[] | undefined) => void;
  selectedItems: ResourceListProps['selectedItems'];
  setSelectedItems: (value: ResourceListProps['selectedItems']) => void;
  themePages: IThemePageManagement[];
  isLoading: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  totalCount: number;
  handleRefetch: () => Promise<void>;
  handleFetchNextPage: () => Promise<void>;
  handleFetchPreviousPage: () => Promise<void>;
  deletingThemePages: IThemePageManagement[];
  setDeletingThemePages: (value: IThemePageManagement[]) => void;
  showDeleteFailBanner: boolean;
  setShowDeleteFailBanner: (value: boolean) => void;
}

interface IThemePageManagementProvider {
  children: ReactNode;
}

export const ThemePageManagementContext = createContext<IThemePageManagementContext | undefined>(
  undefined,
);

export const ThemePageManagementProvider: React.FC<IThemePageManagementProvider> = ({
  children,
}) => {
  const [queryValue, setQueryValue] = useState<string | undefined>(undefined);
  const [pageTypes, setPageTypes] = useState<TemplateType[] | undefined>(undefined);
  const [selectedItems, setSelectedItems] = useState<ResourceListProps['selectedItems']>([]);
  const [deletingThemePages, setDeletingThemePages] = useState<IThemePageManagement[]>([]);
  const [showDeleteFailBanner, setShowDeleteFailBanner] = useState<boolean>(false);

  const {
    data: themePages,
    isLoading,
    isFetching,
    isFetchingNextPage,
    totalCount,
    handleRefetch,
    handleFetchNextPage,
    handleFetchPreviousPage,
  } = useQueryThemePageManagement({
    pageTypes,
    queryValue,
  });

  const contextValue = useMemo(
    () => ({
      queryValue,
      setQueryValue,
      pageTypes,
      setPageTypes,
      selectedItems,
      setSelectedItems,
      themePages,
      isLoading,
      isFetching,
      isFetchingNextPage,
      totalCount,
      handleRefetch,
      handleFetchNextPage,
      handleFetchPreviousPage,
      deletingThemePages,
      setDeletingThemePages,
      showDeleteFailBanner,
      setShowDeleteFailBanner,
    }),
    [
      queryValue,
      pageTypes,
      selectedItems,
      setSelectedItems,
      themePages,
      isLoading,
      isFetching,
      isFetchingNextPage,
      totalCount,
      handleRefetch,
      handleFetchNextPage,
      handleFetchPreviousPage,
      deletingThemePages,
      showDeleteFailBanner,
      setShowDeleteFailBanner,
    ],
  );

  return (
    <ThemePageManagementContext.Provider value={contextValue}>
      {children}
    </ThemePageManagementContext.Provider>
  );
};

export const useThemePageManagementContext = () => {
  const context = useContext(ThemePageManagementContext);
  if (!context) {
    throw new Error(
      'useThemePageManagementContext must be used within a ThemePageManagementProvider',
    );
  }
  return context;
};
